<template>
  <!-- Normal (kebab) render: opens below the three-dots button. -->
  <v-menu
    v-if="!contextActive"
    v-model="isOpen"
    :open-on-hover="resolvedOpenOnHover"
    :location="openRightward ? 'bottom start' : location"
    :offset="-36"
    :location-strategy="locationStrategy"
    :content-class="
      openRightward
        ? 'action-menu-content action-menu-content--rightward'
        : 'action-menu-content'
    "
  >
    <template #activator="{ props }">
      <KebabButton v-bind="props" :disabled="disabled" />
    </template>
    <v-list>
      <slot />
    </v-list>
  </v-menu>

  <!-- Right-click CONTEXT render (see ContextMenuHost): no activator, positioned
       at the cursor anchor TableCard supplies. Vuetify 3 handles flip/clamp near
       screen edges and re-anchors on scroll natively, so the menu stays on-screen,
       follows the row, and has no manual position to snap back from on close. The
       SAME slot items render, so any :disabled / v-model logic in the menu
       component is untouched. -->
  <v-menu
    v-else
    :model-value="ctx.open"
    :open-on-hover="resolvedOpenOnHover"
    :target="ctx.target"
    location="bottom start"
    :location-strategy="locationStrategy"
    content-class="action-context-menu-content"
    @update:model-value="onContextOpenChange"
  >
    <v-list>
      <slot />
    </v-list>
  </v-menu>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { noFlipConnectedLocationStrategy } from '~/cynqueModules/noFlipConnectedLocationStrategy'

/**
 * ActionMenu — the app-wide "three-dots" (kebab) action menu for the SIMPLE ROW menus.
 *
 * Wraps <v-menu> + the shared <KebabButton> activator, exposing a default slot for the
 * <v-list> items (typically <AppMenuItem>s). Replaces this boilerplate, repeated across
 * the row-menu components:
 *
 *   <v-menu :open-on-hover="!$userSettings().doNotShowMenusOnHover">
 *     <template #activator="{ props }">
 *       <v-btn icon variant="text" v-bind="props"><v-icon>mdi-dots-vertical</v-icon></v-btn>
 *     </template>
 *     <v-list> ...items... </v-list>
 *   </v-menu>
 *
 * with:  <ActionMenu :disabled="…"> ...items... </ActionMenu>
 *
 * SCOPE: the ~37 row action menus (open-on-hover per the user setting, flat item list).
 * NOT the 11 title-bar menus (always-click, close-on-content-click=false, nested
 * submenus) — those keep their bespoke <v-menu> but still use <KebabButton>.
 *
 * Sibling to AppMenuItem (which already consolidated the menu *items*).
 */
export default defineComponent({
  name: 'ActionMenu',
  // Optional: a ContextMenuHost ancestor provides this (a computed ref) to make
  // this menu open as a right-click context menu instead of a kebab menu. Absent
  // for the normal kebab render, so `contextActive` is false and nothing changes.
  inject: {
    actionMenuContext: { default: undefined },
  },
  props: {
    // Two-way open state. Optional -- omit for the common uncontrolled case; bind it
    // (v-model) where a parent watches open/close (some menus recompute a disable-flag
    // while shown). NOTE: no `type: Boolean` on purpose -- Vue coerces an *absent*
    // Boolean prop to `false`, which would make an uncontrolled menu look
    // controlled-closed and never open. No type keeps absence as `undefined`.
    modelValue: { default: undefined },
    // Disable the activator (a disabled button can't open the menu).
    disabled: { type: Boolean, default: false },
    // VMenu positioning. V2 opened the row menu OVER its three-dots button
    // (extending down and to the right); V3's default drops it below-left instead.
    // Strategy (see the hard-coded `:offset` and `content-class` in the template,
    // plus `.action-menu-content` in assets/global.css):
    //   - `location="bottom end"` right-aligns the menu to the button. This base
    //     never overflows to the right, so V3's flip/shift leaves it put -- stable
    //     in BOTH scrolling cards (footer -> horizontal scroll) and footerless ones,
    //     where a cross-axis *offset* flips unpredictably.
    //   - `:offset="-36"` lifts it up by the 36px button height to cover the button.
    //   - a fixed CSS `translateX` slides it right to V2's overhang (flip-independent).
    location: { type: String, default: 'bottom end' },
    // Force open-on-hover on/off. Default (undefined) honours the app-wide
    // doNotShowMenusOnHover user setting, as the originals did.
    openOnHover: { default: undefined },
    // Anchor the menu's LEFT edge to the button instead (see
    // `.action-menu-content--rightward` in assets/global.css). The default
    // `bottom end` + translateX(64px) puts the pointer (W - 82)px into a menu W
    // wide: on the first item's ICON for the ~113px Edit/Delete menus it was
    // tuned on, but on its CAPTION once a menu is wider -- PasskeyMenu's
    // "Rename…" (137px) put it 55px in. Anchored at the start, the pointer lands
    // 31px in at any width. Only where the kebab has room to its right: the
    // default exists because a TableCard's kebab sits at the window's edge.
    openRightward: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  data() {
    return { internalOpen: false }
  },
  computed: {
    // The injected context descriptor ({ active, target, open, close }) or undefined.
    // ContextMenuHost provides a plain getter-object (not a ref), so use it directly.
    ctx(): any {
      return (this as any).actionMenuContext
    },
    contextActive(): boolean {
      return !!this.ctx?.active
    },
    // The host's open state, read through the injected descriptor's getter so it
    // is tracked in this component's reactive effects (the watcher below).
    contextOpen(): boolean {
      return !!(this.contextActive && this.ctx?.open)
    },
    // Vuetify 3 flips a menu to the opposite side when it would overflow the
    // viewport; this custom strategy shifts/clamps it inside instead (Vuetify-2
    // behaviour), so a menu opened near the bottom stays under the cursor rather
    // than jumping above it. Applied to BOTH the kebab and the context menus.
    locationStrategy(): typeof noFlipConnectedLocationStrategy {
      return noFlipConnectedLocationStrategy
    },
    isOpen: {
      get(): boolean {
        return this.modelValue === undefined
          ? this.internalOpen
          : (this.modelValue as boolean)
      },
      set(value: boolean) {
        this.internalOpen = value
        this.$emit('update:modelValue', value)
      },
    },
    resolvedOpenOnHover(): boolean {
      if (this.openOnHover !== undefined) {
        return this.openOnHover as boolean
      }
      // @ts-expect-error $userSettings is a globally-injected helper (used the same way
      // in the original menu templates).
      return !this.$userSettings().doNotShowMenusOnHover
    },
  },
  // ⚠ CONTEXT MODE IS DRIVEN FROM A WATCHER, NOT A mounted() HOOK — and there is
  // deliberately no mounted() hook at all. TableCard keeps its ContextMenuHost
  // MOUNTED across closes (GitHub #124: unmounting it left a confirm dialog's
  // callback bound to a dead instance, whose $emit Vue drops in silence), so in
  // context mode this component is created ONCE and then re-pointed at each newly
  // right-clicked row. mount() is therefore the wrong moment twice over: it runs
  // for the first row only, and it now runs while the menu is still CLOSED (the
  // host opens a tick later). Measured with the disabled-check left in mounted():
  // it closed an already-closed menu, the pending open then went ahead, and a
  // DISABLED row's context menu opened — where the kebab correctly refuses to.
  //
  // The same change removes the need for the explicit `update:modelValue` emit
  // that hook also carried. The host now MOUNTS CLOSED and opens a tick later, so
  // Vuetify sees a real false->true on :model-value and emits it; the version
  // that mounted ALREADY open saw no change and had to fake one. What relies on
  // the emit arriving on EVERY open: menus that refetch while shown
  // (OpportunityContractMSServiceMenu looks the contract up to enable "Remove
  // from Contract"), which would otherwise answer for the previous row.
  watch: {
    contextOpen(value: boolean) {
      // A disabled menu can't be opened from its kebab; match that for
      // right-click by closing immediately (which clears the row tint via the
      // host).
      if (value && this.disabled) {
        this.ctx?.close()
      }
    },
  },
  methods: {
    onContextOpenChange(value: boolean) {
      // Keep a bound v-model in sync, then let the host close the menu when
      // Vuetify closes it (click-away / Escape / selecting an item).
      this.$emit('update:modelValue', value)
      if (!value) {
        this.ctx?.close()
      }
    },
  },
})
</script>
